import React, { useEffect } from 'react';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

import styled from 'styled-components';
import { Button, Divider, Tooltip, Typography } from 'antd';
import { EditOutlined, ExpandAltOutlined } from '@ant-design/icons';

import TabToolbar from '../../components/styled/TabToolbar';
import { useTranslation } from '../../../../../i18n/useTranslation';
import { AddLinkModal } from '../../components/styled/AddLinkModal';
import { EmptyTab } from '../../components/styled/EmptyTab';
import { DescriptionEditor } from './components/DescriptionEditor';
import { LinkList } from './components/LinkList';

import { useEntityData, useRefetch, useRouteToTab } from '../../EntityContext';
import { EDITED_DESCRIPTIONS_CACHE_NAME, mapLinkToLinkType } from '../../utils';
import { Editor } from './components/editor/Editor';
import { DescriptionPreviewModal } from './components/DescriptionPreviewModal';
import { sanitizeDescription } from '../../../../shared/utils/sanitizationUtils';

const DocumentationContainer = styled.div`
    padding: 40px 0;
    background-color: #ffffff;
`;

const TooltipWrapper = styled.span<{ isReadOnly?: boolean }>`
    display: inline-block;
    margin-left: ${(props) => (props.isReadOnly ? '8px' : '0px')};
    cursor: ${(props) => (props.isReadOnly ? 'not-allowed' : 'pointer')};
`;

interface Props {
    hideLinksButton?: boolean;
}

export const DocumentationTab = ({ properties }: { properties?: Props }) => {
    const { t } = useTranslation();
    const hideLinksButton = properties?.hideLinksButton;
    const { urn, entityData } = useEntityData();
    const refetch = useRefetch();
    const rawDescription = entityData?.editableProperties?.description || entityData?.properties?.description || '';
    const description = sanitizeDescription(rawDescription);
    const links = entityData?.institutionalMemory?.elements || [];
    const localStorageDictionary = localStorage.getItem(EDITED_DESCRIPTIONS_CACHE_NAME);
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = entityData?.readOnly?.readOnly === true;

    const routeToTab = useRouteToTab();
    const isEditing = queryString.parse(useLocation().search, { parseBooleans: true }).editing;
    const showModal = queryString.parse(useLocation().search, { parseBooleans: true }).modal;

    const existingLinks = mapLinkToLinkType(links);

    useEffect(() => {
        const editedDescriptions = (localStorageDictionary && JSON.parse(localStorageDictionary)) || {};
        if (editedDescriptions.hasOwnProperty(urn)) {
            routeToTab({
                tabName: 'Documentation',
                tabParams: { editing: true, modal: !!showModal },
            });
        }
    }, [urn, routeToTab, showModal, localStorageDictionary]);

    return isEditing && !showModal && !isReadOnly ? (
        <DocumentationContainer className="py-0">
            <DescriptionEditor onComplete={() => routeToTab({ tabName: 'Documentation' })} />
        </DocumentationContainer>
    ) : (
        <>
            {description || links.length ? (
                <>
                    <TabToolbar>
                        <div>
                            <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                                <Button
                                    type="default"
                                    className="mr-2"
                                    aria-label={t('Edit')}
                                    data-testid="edit-documentation-button"
                                    onClick={() =>
                                        routeToTab({ tabName: 'Documentation', tabParams: { editing: true } })
                                    }
                                    disabled={isReadOnly}
                                >
                                    <EditOutlined aria-hidden="true" /> {t('Edit')}
                                </Button>
                            </Tooltip>
                            {!hideLinksButton && (
                                <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                                    <TooltipWrapper isReadOnly={isReadOnly}>
                                        <AddLinkModal
                                            buttonProps={{ type: 'text', disabled: isReadOnly }}
                                            refetch={refetch}
                                            existingLinks={existingLinks}
                                        />
                                    </TooltipWrapper>
                                </Tooltip>
                            )}
                        </div>
                        <div>
                            <Button
                                type="text"
                                onClick={() =>
                                    routeToTab({
                                        tabName: 'Documentation',
                                        tabParams: { modal: true },
                                    })
                                }
                            >
                                <ExpandAltOutlined />
                            </Button>
                        </div>
                    </TabToolbar>

                    <DocumentationContainer className="py-4">
                        {description ? (
                            <div className="px-4">
                                <Editor content={description} readOnly />
                            </div>
                        ) : (
                            <div className="px-4">
                                <Typography.Text className="f-text-medium-content f-color-dark-black-s100">
                                    {t('No documentation added yet.')}
                                </Typography.Text>
                            </div>
                        )}
                        <Divider />
                        {!hideLinksButton && (
                            <div className="px-4">
                                <LinkList refetch={refetch} readOnly={isReadOnly} />
                            </div>
                        )}
                    </DocumentationContainer>
                </>
            ) : (
                <EmptyTab tab="documentation">
                    <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                        <Button
                            onClick={() => routeToTab({ tabName: 'Documentation', tabParams: { editing: true } })}
                            aria-label={t('Add Documentation')}
                            disabled={isReadOnly}
                        >
                            <EditOutlined aria-hidden="true" /> {t('Add Documentation')}
                        </Button>
                    </Tooltip>
                    {!hideLinksButton && (
                        <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                            <TooltipWrapper isReadOnly={isReadOnly}>
                                <AddLinkModal
                                    refetch={refetch}
                                    existingLinks={existingLinks}
                                    buttonProps={{ disabled: isReadOnly }}
                                />
                            </TooltipWrapper>
                        </Tooltip>
                    )}
                </EmptyTab>
            )}
            {showModal && (
                <DescriptionPreviewModal
                    editMode={(isEditing && !isReadOnly) || false}
                    description={description}
                    onClose={() => {
                        routeToTab({ tabName: 'Documentation', tabParams: { editing: false } });
                    }}
                />
            )}
        </>
    );
};
